// products.utils.ts
import { useEffect, RefObject } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useSelector } from 'react-redux'
import { setCookie, getCookie } from 'cookies-next'
import { IRedux } from '@/app/[locale]/_store/types'
import { isTenantLocaleSettled } from '@/app/[locale]/_utils/shipper-switch'

// Re-export hooks for convenient imports
export { useInViewport } from '../hooks/use-in-viewport'
export { useVisibleProductsQuery } from '../hooks/use-visible-products-query'

// Constants
export const PRODUCT_FIELDS = [
  'image',
  'description',
  'barcode',
  'product_type',
  'ikpu',
  'package_code',
  'tags',
  'type',
  'slug',
  'has_modifier',
  'weight',
  'measurement',
]

// Type definitions

export interface MenuContext {
  platform_id: string
  effectiveMenuId?: string
  deliveryType: string
  is_hall: string
}

export interface InfiniteScrollOptions {
  threshold: number
}

export interface FilteredQueryParams {
  tag_ids?: string
  sort?: string
  effectiveMenuId?: string
  deliveryType?: string
  category_id?: string
  limit?: number
  source_id?: string
}

export interface FilterParams {
  tagIds?: string
  sort?: string
  categoryId?: string
  isFiltered: boolean
}

/**
 * Custom hook to manage filter parameters
 */
export const useFilterParams = (): FilterParams => {
  const searchParams = useSearchParams()
  const tagIds = searchParams.get('tag_ids') || undefined
  const sort = searchParams.get('sort') || undefined

  const isFiltered = Boolean(tagIds || sort)

  return { tagIds, sort, isFiltered }
}

/**
 * Custom hook to get menu ID and delivery type from Redux state
 * Also syncs values to cookies for server-side access
 */
export const useMenuContext = (initialMenuId?: string): MenuContext => {
  const router = useRouter()

  const { branch } = useSelector((state: IRedux) => state.common)
  const defaultMenuFromSettings = useSelector(
    (state: IRedux) => state.settings.data?.data?.default_menu?.value
  )
  const { platform_id, is_hall, menu_id, order_type } = useSelector(
    (state: IRedux) => state.session
  )
  const settingsShipperId = useSelector(
    (state: IRedux) => state.settings.data?.shipper_id
  )

  const defaultMenuIdCookie = getCookie('default_menu_id')

  const effectiveMenuId =
    is_hall === 'true'
      ? menu_id !== 'null'
        ? menu_id
        : undefined
      : branch?.menu_id ||
        defaultMenuFromSettings ||
        initialMenuId ||
        (typeof defaultMenuIdCookie === 'string'
          ? defaultMenuIdCookie
          : undefined)

  const deliveryType = order_type?.type || ''

  // Sync menu_id cookie for server-side access
  useEffect(() => {
    const cookieOptions = {
      maxAge: 30 * 24 * 60 * 60, // 30 days
      path: '/',
    }

    // Get current menu_id cookie value
    const currentMenuId = getCookie('menu_id')
    const newMenuId = menu_id && menu_id !== 'null' ? menu_id : branch?.menu_id

    // Set menu_id cookie only if it changed
    if (newMenuId && currentMenuId !== newMenuId) {
      setCookie('menu_id', newMenuId, cookieOptions)
      if (settingsShipperId && isTenantLocaleSettled(settingsShipperId)) {
        router.refresh()
      }
    }

    // Set is_hall cookie
    if (is_hall) {
      setCookie('is_hall', is_hall, cookieOptions)
    }

    // Keep cookie in sync (saveOrderType also writes it; soft refresh is handled by
    // useHomeOrderTypeSync comparing SSR params.delivery_type so we don't miss updates).
    if (deliveryType) {
      setCookie('delivery_type', deliveryType, cookieOptions)
    }
  }, [menu_id, branch?.menu_id, is_hall, deliveryType, settingsShipperId, router])

  return {
    platform_id,
    effectiveMenuId,
    deliveryType,
    is_hall,
  }
}

/**
 * Custom hook for infinite scroll
 */
export const useInfiniteScroll = (
  ref: RefObject<HTMLElement | null>,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  fetchNextPage: () => void,
  options: InfiniteScrollOptions = { threshold: 0.5 }
): void => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    }, options)

    const currentRef = ref.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
      observer.disconnect()
    }
  }, [ref, fetchNextPage, hasNextPage, isFetchingNextPage, options.threshold])
}
